<template>
  <div
    class="motor-performance-chart"
    :class="{ 'is-static': isStatic, 'is-horizontal': isHorizontal }"
  >
    <DDV
      v-slot="{
        mouseState,
        margins,
        onUpdateNearestPoint,
        enableMouseTracking
      }"
      v-bind="$attrs"
      @update:dom-state="domState = $event"
    >
      <template
        v-if="
          domState &&
          margins &&
          domState.plotWidth > 0 &&
          domState.plotHeight > 0
        "
      >
        <rect
          v-if="domState.plotWidth > 0 && domState.plotHeight > 0"
          class="plotBg"
          :x="margins.left"
          :y="margins.top"
          :width="domState.plotWidth"
          :height="domState.plotHeight"
        />
        <template
          v-if="
            chartData &&
            percentageBasedDataSets.some(ds => ds.points.length > 3)
          "
        >
          <!-- Percentages -->
          <CartesianScaler
            v-if="domState.plotWidth > 0"
            v-slot="{ xScaler, yScaler, pointScaler }"
            :x-source="chartData.xDomain"
            :y-source="aggregatePercentageBasedDomains.aggregateY.value"
            :x-target="targetDomainX"
            :y-target="targetDomainY"
            reverse-y-axis
          >
            <!-- X Axis -->
            <Axis
              label="Percent Motor Load"
              :x="margins.left"
              :y="margins.top"
              :scaler="xScaler"
              :domain="chartData.xDomain"
              :margin-px="6"
              draw-grid-lines
              :tick-frequency="ddvTickFrequencies?.x"
            />

            <Axis
              label="% Motor Eff, % Current, PF"
              :x="margins.left"
              :y="margins.top"
              :scaler="yScaler"
              :domain="aggregatePercentageBasedDomains.aggregateY.value"
              :margin-px="6"
              :tick-frequency="ddvTickFrequencies?.y"
              is-vertical
            />

            <g :transform="`translate(${margins.left} ${margins.top})`">
              <ThresholdLine
                v-if="typeof operatingPointThreshold?.value === 'number'"
                is-x
                :scaler="xScaler"
                :domain="{ min: 0, max: 0 }"
                :value="operatingPointThreshold.value"
              />

              <!-- Eff/PF/Percent Current datasets -->
              <BSP
                v-for="(ds, i) in percentageBasedDataSets"
                v-slot="{ screenPoints, nearestPoint }"
                :key="`line-plot-${i}`"
                :points="ds.points"
                :screen-points="ds.points.map(pointScaler.scale)"
                ignore-y-values
                :track-nearest-point="enableMouseTracking && activeItems[i]"
                @update:nearest-point="
                  onUpdateNearestPoint(
                    ds.title ?? '',
                    i,
                    activeItems[i],
                    $event,
                    formatValue,
                    formatValue,
                    ds.xAxisLabel,
                    ds.yAxisLabel
                  )
                "
              >
                <LinePlot
                  v-if="screenPoints.length > 3"
                  :is-active="activeItems[i]"
                  :data-series-number="i"
                  :x="margins.left"
                  :y="margins.top"
                  :screen-points="screenPoints"
                  curve-interpolator="catmull-rom"
                />
                <NearestPoint
                  v-if="nearestPoint?.node"
                  :data-series-number="i"
                  :nearest-point="nearestPoint"
                />
              </BSP>
              <!-- Eff/PF/Current operating points on mid line -->
              <g
                v-for="p in chartData.percentageBasedOperatingPoints"
                :key="p.label"
                :transform="`
                translate(
                  ${xScaler.scale(p.point.x)},
                  ${yScaler.scale(p.point.y)}
                )`"
              >
                <g>
                  <circle
                    :cx="0"
                    :cy="0"
                    :r="5"
                    :stroke="p.color"
                    :stroke-width="2"
                    fill="transparent"
                  />
                  <circle
                    :cx="0"
                    :cy="0"
                    :r="5"
                    :stroke="p.color"
                    :stroke-opacity="0.3"
                    :stroke-width="8"
                    fill="transparent"
                  />
                  <circle :cx="0" :cy="0" :r="4" fill="var(--plotSurface)" />
                </g>
              </g>
            </g>
          </CartesianScaler>

          <!-- Speed RPM y-axis/datasets/operating points -->
          <CartesianScaler
            v-if="domState.plotWidth > 0"
            v-slot="{ xScaler, yScaler, pointScaler }"
            :x-source="chartData.xDomain"
            :y-source="chartData.paddedSpeedRpmDomain"
            :x-target="targetDomainX"
            :y-target="targetDomainY"
            reverse-y-axis
          >
            <Axis
              label="Speed RPM"
              :x="margins.left + domState.plotWidth + 5"
              :y="margins.top"
              :scaler="yScaler"
              :domain="chartData.paddedSpeedRpmDomain"
              :margin-px="6"
              :tick-frequency="ddvTickFrequencies?.y"
              is-vertical
              is-right
            />
            <!--Speed RPM dataset-->
            <g :transform="`translate(${margins.left} ${margins.top})`">
              <BSP
                v-slot="{ screenPoints, nearestPoint }"
                :points="dataSets[3].points"
                :screen-points="dataSets[3].points.map(pointScaler.scale)"
                ignore-y-values
                :track-nearest-point="enableMouseTracking && activeItems[3]"
                @update:nearest-point="
                  onUpdateNearestPoint(
                    dataSets[3].title ?? '',
                    3,
                    activeItems[3],
                    $event,
                    formatValue,
                    formatValue,
                    dataSets[3].xAxisLabel,
                    dataSets[3].yAxisLabel
                  )
                "
              >
                <LinePlot
                  v-if="screenPoints.length > 3"
                  :is-active="activeItems[3]"
                  :data-series-number="3"
                  :x="margins.left"
                  :y="margins.top"
                  :screen-points="screenPoints"
                  curve-interpolator="catmull-rom"
                />

                <NearestPoint
                  v-if="nearestPoint?.node"
                  :data-series-number="3"
                  :nearest-point="nearestPoint"
                />
              </BSP>
              <g
                :key="chartData.speedRpmOperatingPoint.label"
                :transform="`
                translate(
                  ${xScaler.scale(chartData.speedRpmOperatingPoint.point.x)},
                  ${yScaler.scale(chartData.speedRpmOperatingPoint.point.y)}
                )
              `"
              >
                <g>
                  <circle
                    :cx="0"
                    :cy="0"
                    :r="5"
                    :stroke="chartData.speedRpmOperatingPoint.color"
                    :stroke-width="2"
                    fill="transparent"
                  />
                  <circle
                    :cx="0"
                    :cy="0"
                    :r="5"
                    :stroke="chartData.speedRpmOperatingPoint.color"
                    :stroke-opacity="0.3"
                    :stroke-width="8"
                    fill="transparent"
                  />
                  <circle :cx="0" :cy="0" :r="4" fill="var(--plotSurface)" />
                </g>
              </g>
            </g>
          </CartesianScaler>
        </template>
        <text
          v-else
          x="50%"
          y="50%"
          dominant-baseline="middle"
          text-anchor="middle"
          fill="var(--plotColor0)"
          >No Data</text
        >
        <Crosshairs
          v-if="readyToDraw(enableMouseTracking, mouseState.isHovered)"
          ignore-y
          :x="margins.left"
          :y="margins.top"
        />
      </template>

      <ChartTitle v-if="title" :title="title" />
    </DDV>
    <div v-if="drawMotorDetails && chartData">
      <span>
        <b
          >{{ chartData.motor.manufacturer }}
          {{ chartData.motor.part_number }}</b
        >
      </span>
      <div class="grid">
        <ConverterInput
          class="col"
          label="Nameplate HP"
          :model-value="chartData.adjustedReratedNameplateHP"
          :base-unit="PowerUnits.HorsePower"
          :model="[PowerUnits.HorsePower]"
          mode="read"
        />
        <ConverterInput
          class="col"
          label="Nameplate Volts"
          :model-value="chartData.adjustedReratedNameplateV"
          :base-unit="VoltageUnits.Volts"
          :model="[VoltageUnits.Volts]"
          mode="read"
        />
        <ConverterInput
          class="col"
          label="Nameplate Amps"
          :model-value="chartData.adjustedReratedNameplateA"
          :base-unit="CurrentUnits.Amperes"
          :model="[CurrentUnits.Amperes]"
          mode="read"
        />
      </div>
    </div>
    <div class="pl-3 pt-1">Operating Points</div>
    <CustomLegend
      v-if="drawLegend && chartData"
      v-model="activeItems"
      :model="allOperatingPoints"
      shared-x-label="% Load"
      :shared-x-value="
        typeof operatingPointThreshold?.value === 'number'
          ? operatingPointThreshold.value
          : undefined
      "
      :static="isStatic"
      :horizontal="isHorizontal"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";
import type { DOMState } from "@incytestudios/ddv/types/common";
import {
  DDV,
  BSP,
  Axis,
  ChartTitle,
  LinePlot,
  Crosshairs,
  NearestPoint,
  CartesianScaler,
  useAggregateDomains,
  ThresholdLine,
  getDefaultValueFormatter,
  fromRange
} from "@incytestudios/ddv";
import CustomLegend from "./CustomLegend.vue";
import type {
  Equipment,
  OperatingParameters
} from "@/composables/forms/formTypes";
import type { OperatingPoint } from "@/components/types";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import { PowerUnits } from "@/libs/units/power";
import { CurrentUnits } from "@/libs/units/current";
import { VoltageUnits } from "@/libs/units/voltage";
import ConverterInput from "@/components/ConverterInput.vue";
import { useMotorPerformanceChartData } from "@/libs/charting/motorPerformance/useMotorPerformanceChartData";
import {
  equipmentKey,
  parametersKey,
  scenarioCalculationsKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";

const props = defineProps<{
  title?: string;
  drawLegend?: boolean;
  drawMotorDetails?: boolean;
  isStatic?: boolean;
  isHorizontal?: boolean;
  equipment?: Equipment;
  parameters?: OperatingParameters;
  scenarioCalculations?: ScenarioCalculations;
}>();
const _equipment = props.equipment
  ? ref(props.equipment)
  : injectStrict(equipmentKey);
const _parameters = props.parameters
  ? ref(props.parameters)
  : injectStrict(parametersKey);
const _calcs =
  props.scenarioCalculations || injectStrict(scenarioCalculationsKey);

const chartData = computed(() =>
  useMotorPerformanceChartData(_equipment.value, _parameters.value, _calcs)
);

const readyToDraw = (enableMouseTracking: boolean, isHovered: boolean) => {
  return (
    enableMouseTracking &&
    isHovered &&
    percentageBasedDataSets.value.some(ds => ds.points.length > 3)
  );
};

const domState = ref<DOMState>();

const targetDomainX = computed(() => fromRange(domState.value?.plotWidth || 0));
const targetDomainY = computed(() =>
  fromRange(domState.value?.plotHeight || 0)
);

const formatValue = getDefaultValueFormatter();

const ddvTickFrequencies = inject<{ x: string; y: string }>(
  "ddvTickFrequencies"
);

const fakeInactiveDataSets = ref([]);

const absoluteScale = ref(false);

const dataSets = computed(() =>
  chartData.value
    ? [
        chartData.value?.motorEfficiencyDataSet,
        chartData.value?.motorPowerFactorDataSet,
        chartData.value?.motorCurrentDataSet,
        chartData.value?.motorNameplateCurrentDataSet
      ]
    : []
);

const percentageBasedDataSets = computed(() =>
  chartData.value
    ? [
        chartData.value?.motorEfficiencyDataSet,
        chartData.value?.motorPowerFactorDataSet,
        chartData.value?.motorCurrentDataSet
      ]
    : []
);

const aggregatePercentageBasedDomains = useAggregateDomains(
  percentageBasedDataSets,
  fakeInactiveDataSets,
  absoluteScale
);

const allOperatingPoints = computed<OperatingPoint[]>(() => [
  ...(chartData.value?.percentageBasedOperatingPoints || []).map(point => ({
    ...point,
    hideXDetails: true
  })),
  chartData.value?.speedRpmOperatingPoint
    ? {
        ...chartData.value.speedRpmOperatingPoint,
        hideXDetails: true
      }
    : {
        label: "",
        point: { x: 0, y: 0 },
        hideXDetails: true
      }
]);

const operatingPointThreshold = computed(
  () => chartData.value?.operatingPointThreshold
);

const activeItems = ref(allOperatingPoints.value.map(() => true));
</script>

<style lang="scss" scoped>
.motor-performance-chart {
  display: grid;
  grid-template-columns: 3fr max-content;

  &.is-horizontal {
    grid-template-columns: 1fr;
  }

  &.is-static {
    :deep(.ddv) {
      &.isPlotHovered {
        cursor: auto !important;
      }
    }
  }
}
</style>
